import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { Check, Repeat, Clock } from 'lucide-react';
import {
  addDays, addMinutes, startOfDay, sameDay, ymd, layoutDay, laneAllDay, minutesOfDay, fmtTime, fmtRange, fmtHourLabel, weekdayName,
} from './calendarMath';
import { tint } from './palette';

// Day and week views: an all-day row over a 24-hour grid, Google style.
//   Drag on empty time  → a new appointment for that range (a click books an hour)
//   Drag an appointment → move it (across days too); drag its bottom edge → change the end
//   Click an appointment → its details
// Everything snaps to 15 minutes. Escape cancels a drag. On touch screens a tap books or opens
// (no drag, so the grid still scrolls under a finger).
export const HOUR_PX = 48;
const GRID_PX = 24 * HOUR_PX;
const MIN_PX = HOUR_PX / 60;
const ALLDAY_LANES = 3;

export default function TimeGrid({ days, occs, allDay, colorFor, onSelect, onCreate, onChange, onDayClick, now, selectedKey, draft }) {
  const scrollRef = useRef(null);
  const colsRef = useRef(null);
  const drag = useRef(null);
  const [ghost, setGhost] = useState(null); // { mode, dayIdx, top, height, key }
  const [allDayOpen, setAllDayOpen] = useState(false);
  const n = days.length;

  // Open at 7am, or an hour and a half before now when today is on screen. Only when the days
  // change, not on every render.
  const firstDay = ymd(days[0]);
  const showsToday = days.some((d) => sameDay(d, new Date()));
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const mins = showsToday ? Math.max(0, minutesOfDay(new Date()) - 90) : 7 * 60;
    el.scrollTop = mins * MIN_PX;
  }, [firstDay, n, showsToday]);
  // The grid scrolls and the headings don't: pad the headings by the scrollbar so columns line up.
  const [sbw, setSbw] = useState(0);
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const measure = () => setSbw(el.offsetWidth - el.clientWidth);
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(el);
    return () => ro && ro.disconnect();
  }, []);

  const perDay = useMemo(() => days.map((d) => layoutDay(occs, d)), [days, occs]);
  const bars = useMemo(() => laneAllDay(allDay, days), [allDay, days]);
  const lanes = bars.reduce((m, b) => Math.max(m, b.lane + 1), 0);
  const shownLanes = allDayOpen ? lanes : Math.min(lanes, ALLDAY_LANES);
  const hiddenByDay = useMemo(() => days.map((_, i) => bars.filter((b) => b.lane >= shownLanes && b.startIdx <= i && b.endIdx >= i).length), [bars, days, shownLanes]);

  // Pointer → grid position.
  const at = useCallback((e) => {
    const r = colsRef.current.getBoundingClientRect();
    const dayIdx = Math.min(n - 1, Math.max(0, Math.floor(((e.clientX - r.left) / r.width) * n)));
    const minute = Math.min(24 * 60, Math.max(0, (e.clientY - r.top) / MIN_PX));
    return { dayIdx, minute };
  }, [n]);

  const finish = useCallback((commit) => {
    const d = drag.current;
    drag.current = null;
    setGhost(null);
    if (!d || !commit) return;
    if (d.mode === 'click') { if (!d.moved) onSelect(d.occ, d.el.getBoundingClientRect()); return; }
    if (d.touch && d.moved) return; // a finger that scrolled books nothing
    const g = d.ghost;
    if (d.mode === 'create') {
      const start = addMinutes(startOfDay(days[g.dayIdx]), g.top);
      const end = addMinutes(start, d.moved ? g.height : 60);
      onCreate({ start, end, allDay: false }, d.anchorRect());
    } else if (!d.moved) {
      onSelect(d.occ, d.el.getBoundingClientRect());
    } else {
      const start = addMinutes(startOfDay(days[g.dayIdx]), g.top);
      const end = d.mode === 'move' ? new Date(start.getTime() + (d.occ.end - d.occ.start)) : addMinutes(start, g.height);
      if (start.getTime() !== d.occ.start.getTime() || end.getTime() !== d.occ.end.getTime()) onChange(d.occ, start, end);
    }
  }, [days, onChange, onCreate, onSelect]);

  useEffect(() => {
    const key = (e) => { if (e.key === 'Escape' && drag.current) { e.stopPropagation(); finish(false); } };
    window.addEventListener('keydown', key, true);
    return () => window.removeEventListener('keydown', key, true);
  }, [finish]);

  const onPointerMove = (e) => {
    const d = drag.current;
    if (!d) return;
    if (!d.moved && Math.hypot(e.clientX - d.x0, e.clientY - d.y0) < (d.touch ? 8 : 4)) return;
    d.moved = true;
    if (d.mode === 'click' || d.touch) return;
    const p = at(e);
    let g;
    if (d.mode === 'create') {
      // From the slot pressed to the slot under the pointer, either direction.
      const a = d.startMin;
      const slot = Math.min(24 * 60 - 15, Math.floor(p.minute / 15) * 15);
      const top = Math.min(a, slot);
      g = { mode: 'create', dayIdx: d.dayIdx, top, height: Math.max(a, slot) + 15 - top };
    } else if (d.mode === 'move') {
      const dur = Math.round((d.occ.end - d.occ.start) / 60000);
      const top = Math.min(24 * 60 - 15, Math.max(0, Math.round((p.minute - d.grab) / 15) * 15));
      g = { mode: 'move', dayIdx: p.dayIdx, top, height: Math.min(dur, 24 * 60 - top), key: d.occ.key };
    } else {
      const top = d.top;
      const end = Math.max(top + 15, Math.round(p.minute / 15) * 15);
      g = { mode: 'resize', dayIdx: d.dayIdx, top, height: end - top, key: d.occ.key };
    }
    d.ghost = g;
    setGhost(g);
    // Keep the grid scrolling when the pointer reaches its edge.
    const sr = scrollRef.current.getBoundingClientRect();
    if (e.clientY < sr.top + 28) scrollRef.current.scrollTop -= 12;
    else if (e.clientY > sr.bottom - 28) scrollRef.current.scrollTop += 12;
  };
  const onPointerUp = () => { if (drag.current) finish(true); };

  const startCreate = (e, dayIdx) => {
    if (e.button !== 0) return;
    const p = at(e);
    const startMin = Math.min(24 * 60 - 15, Math.floor(p.minute / 15) * 15);
    const touch = e.pointerType === 'touch';
    const g = { mode: 'create', dayIdx, top: startMin, height: 15 };
    drag.current = {
      mode: 'create', touch, dayIdx, startMin, x0: e.clientX, y0: e.clientY, moved: false, ghost: g,
      anchorRect: () => {
        const el = colsRef.current.querySelector('[data-ghost]');
        return el ? el.getBoundingClientRect() : { left: e.clientX, right: e.clientX, top: e.clientY, bottom: e.clientY, width: 0, height: 0 };
      },
    };
    if (touch) return; // a tap books on pointerup; a scroll cancels
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setGhost(g);
  };
  const startEvent = (e, occ, mode, dayIdx, item) => {
    e.stopPropagation();
    if (e.button !== 0) return;
    const touch = e.pointerType === 'touch';
    const p = at(e);
    drag.current = {
      mode: occ.readOnly || touch ? 'click' : mode, occ, el: e.currentTarget.closest('[data-occ]') || e.currentTarget,
      dayIdx, top: item.top, grab: p.minute - item.top, x0: e.clientX, y0: e.clientY, moved: false,
      ghost: { dayIdx, top: item.top, height: item.height },
    };
    if (touch || occ.readOnly) return; // tap: handled on pointerup without capture
    colsRef.current.setPointerCapture?.(e.pointerId);
  };

  const today = now || new Date();
  const nowTop = minutesOfDay(today) * MIN_PX;

  return (
    <div className="flex flex-col h-full min-h-0 bg-white">
      {/* Day headings + all-day row */}
      <div className="flex border-b border-slate-200" style={{ paddingRight: sbw }}>
        <div className="w-14 sm:w-16 flex-shrink-0 flex flex-col justify-end pb-1 pr-2 text-right">
          {lanes > ALLDAY_LANES && (
            <button type="button" onClick={() => setAllDayOpen((v) => !v)} className="text-[11px] font-semibold text-indigo-600 hover:underline" aria-expanded={allDayOpen}>
              {allDayOpen ? 'Less' : 'More'}
            </button>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="grid" style={{ gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))` }}>
            {days.map((d) => {
              const isToday = sameDay(d, today);
              return (
                <div key={ymd(d)} className="flex flex-col items-center pt-2 pb-1.5 border-l border-slate-100 first:border-l-transparent">
                  <span className={clsx('text-[11px] font-semibold tracking-[0.06em] uppercase', isToday ? 'text-indigo-600' : 'text-slate-500')}>{weekdayName(d.getDay()).slice(0, 3)}</span>
                  <button type="button" onClick={() => onDayClick(d)} aria-label={`Show ${weekdayName(d.getDay())} ${d.getDate()}`}
                    className={clsx('mt-0.5 w-10 h-10 rounded-full text-[22px] leading-none font-medium tabular-nums transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
                      isToday ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'text-slate-800 hover:bg-slate-100')}>
                    {d.getDate()}
                  </button>
                </div>
              );
            })}
          </div>
          {/* All-day lanes */}
          <div className="relative" style={{ height: Math.max(1, shownLanes) * 24 + (hiddenByDay.some(Boolean) ? 18 : 0) + 6 }}>
            <div className="absolute inset-0 grid" style={{ gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))` }}>
              {days.map((d, i) => (
                <div key={ymd(d)} data-date={ymd(d)} className="border-l border-slate-100 first:border-l-transparent cursor-pointer hover:bg-slate-50/70"
                  onClick={(e) => { if (e.target === e.currentTarget) onCreate({ start: startOfDay(d), end: addDays(startOfDay(d), 1), allDay: true }, e.currentTarget.getBoundingClientRect()); }}>
                  {hiddenByDay[i] > 0 && (
                    <button type="button" onClick={() => setAllDayOpen(true)} className="absolute text-[11.5px] font-semibold text-slate-600 hover:text-slate-900 px-2"
                      style={{ top: shownLanes * 24 + 2, left: `${(i / n) * 100}%` }}>{hiddenByDay[i]} more</button>
                  )}
                </div>
              ))}
            </div>
            {bars.filter((b) => b.lane < shownLanes).map((b) => (
              <AllDayBar key={b.key} bar={b} n={n} colorFor={colorFor} selected={selectedKey === b.key}
                onClick={(e) => onSelect(b, e.currentTarget.getBoundingClientRect())} onDrop={onChange} days={days} />
            ))}
          </div>
        </div>
      </div>

      {/* Time grid */}
      <div ref={scrollRef} data-cal-grid className="flex-1 min-h-0 overflow-y-auto overscroll-contain sc-cal-scroll">
        <div className="flex" style={{ height: GRID_PX }}>
          <div className="w-14 sm:w-16 flex-shrink-0 relative select-none" aria-hidden>
            {Array.from({ length: 24 }, (_, h) => (
              <span key={h} className="absolute right-2 -translate-y-1/2 text-[11px] text-slate-500 tabular-nums" style={{ top: h * HOUR_PX }}>{fmtHourLabel(h)}</span>
            ))}
          </div>
          <div ref={colsRef} className="flex-1 min-w-0 relative grid touch-pan-y" style={{ gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))` }}
            onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={() => finish(false)}>
            {/* hour lines */}
            <div className="absolute inset-0 pointer-events-none" aria-hidden
              style={{ backgroundImage: `linear-gradient(to bottom, rgb(226 232 240) 1px, transparent 1px)`, backgroundSize: `100% ${HOUR_PX}px` }} />
            {days.map((d, i) => {
              const isToday = sameDay(d, today);
              const items = perDay[i];
              return (
                <div key={ymd(d)} className={clsx('relative border-l border-slate-200/80 first:border-l-transparent', isToday && 'bg-indigo-50/25')}
                  onPointerDown={(e) => startCreate(e, i)}>
                  {items.map((it) => {
                    const hidden = ghost && ghost.key === it.key && ghost.mode === 'move';
                    return (
                      <EventBlock key={it.key} item={it} colorFor={colorFor} now={today} dim={hidden} selected={selectedKey === it.key}
                        onPointerDown={(e, mode) => startEvent(e, it, mode, i, it)}
                        onKeyOpen={(el) => onSelect(it, el.getBoundingClientRect())}
                        resizeHeight={ghost && ghost.key === it.key && ghost.mode === 'resize' ? ghost.height : null} />
                    );
                  })}
                  {/* the appointment being booked (popover open) */}
                  {draft && !ghost && sameDay(draft.start, d) && !draft.allDay && (
                    <div data-ghost className="absolute left-0.5 right-1.5 rounded-md bg-indigo-600/90 text-white text-[12px] font-semibold px-2 py-1 shadow-md pointer-events-none"
                      style={{ top: minutesOfDay(draft.start) * MIN_PX, height: Math.max(18, ((draft.end - draft.start) / 60000) * MIN_PX - 2) }}>
                      {draft.title || '(No title)'}<div className="font-normal opacity-90">{fmtRange(draft.start, draft.end)}</div>
                    </div>
                  )}
                  {ghost && ghost.dayIdx === i && ghost.mode !== 'resize' && (
                    <div data-ghost className={clsx('absolute left-0.5 right-1.5 rounded-md px-2 py-1 text-[12px] font-semibold pointer-events-none shadow-lg',
                      ghost.mode === 'create' ? 'bg-indigo-600/90 text-white' : 'bg-white ring-2 ring-indigo-500 text-slate-800')}
                      style={{ top: ghost.top * MIN_PX, height: Math.max(18, ghost.height * MIN_PX - 2) }}>
                      {ghost.mode === 'create' ? '(No title)' : (drag.current?.occ?.title || '')}
                      <div className="font-normal opacity-90">{fmtRange(addMinutes(startOfDay(d), ghost.top), addMinutes(startOfDay(d), ghost.top + ghost.height))}</div>
                    </div>
                  )}
                  {isToday && (
                    <div className="absolute left-0 right-0 z-[3] pointer-events-none" style={{ top: nowTop }} aria-hidden>
                      <div className="relative h-0.5 bg-rose-500"><span className="absolute -left-1.5 -top-[5px] w-3 h-3 rounded-full bg-rose-500" /></div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// One timed appointment (or the start of one) in a day column.
function EventBlock({ item, colorFor, now, dim, selected, onPointerDown, onKeyOpen, resizeHeight }) {
  const c = colorFor(item);
  const past = item.end < now;
  const cancelled = item.status === 'cancelled';
  const h = Math.max(18, (resizeHeight != null ? resizeHeight : item.height) * MIN_PX - 2);
  const short = h < 34;
  const width = 100 / item.cols;
  const label = `${item.title}, ${fmtRange(item.start, item.end)}${item.participant ? `, ${item.participant}` : ''}${cancelled ? ', cancelled' : ''}`;
  return (
    <div data-occ role="button" tabIndex={0} aria-label={label}
      onPointerDown={(e) => onPointerDown(e, 'move')}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onKeyOpen(e.currentTarget); } }}
      className={clsx('absolute rounded-md px-1.5 overflow-hidden text-left cursor-pointer select-none transition-[box-shadow,opacity] outline-none',
        'focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-indigo-500',
        selected ? 'shadow-lg z-[4] ring-2 ring-white' : 'z-[2] hover:shadow-md', dim && 'opacity-30')}
      style={{
        top: item.top * MIN_PX, height: h,
        left: `calc(${item.col * width}% + 1px)`, width: `calc(${width}% - ${item.cols > 1 ? 3 : 8}px)`,
        background: cancelled ? tint(c.hex, 0.12) : past ? tint(c.hex, 0.72) : c.hex,
        color: cancelled ? c.hex : c.ink,
        boxShadow: item.cols > 1 ? '0 0 0 1px #fff' : undefined,
      }}>
      <div className={clsx('flex items-start gap-1 text-[12px] leading-[16px] pt-0.5', cancelled && 'line-through')}>
        {item.status === 'completed' && <Check size={12} className="mt-0.5 flex-shrink-0" aria-hidden />}
        <span className="truncate"><span className="font-semibold">{item.title}</span>{short && <span className="opacity-90">, {fmtTime(item.start)}</span>}</span>
      </div>
      {!short && (
        <div className="text-[11.5px] leading-[15px] opacity-90 truncate flex items-center gap-1">
          {fmtRange(item.start, item.end)}
          {item.recurring && <Repeat size={10} aria-hidden />}
          {item.time_entry_id && <Clock size={10} aria-hidden />}
        </div>
      )}
      {!short && h > 50 && (item.participant || item.location) && (
        <div className="text-[11.5px] leading-[15px] opacity-90 truncate">{item.participant || item.location}</div>
      )}
      {!item.readOnly && !cancelled && (
        <div className="absolute left-0 right-0 bottom-0 h-1.5 cursor-ns-resize" aria-hidden
          onPointerDown={(e) => onPointerDown(e, 'resize')} />
      )}
    </div>
  );
}

// An all-day bar (all-day appointment or a key date) across the days it covers. Appointments can
// be dragged to another day; key dates are read-only.
function AllDayBar({ bar, n, colorFor, selected, onClick, onDrop, days }) {
  const c = colorFor(bar);
  const ref = useRef(null);
  const st = useRef(null);
  const touched = useRef(false);
  const [over, setOver] = useState(null);
  const left = (bar.startIdx / n) * 100;
  const width = ((bar.endIdx - bar.startIdx + 1) / n) * 100;
  const key = bar.kind === 'keydate';
  const down = (e) => {
    touched.current = e.pointerType === 'touch';
    if (e.button !== 0 || bar.readOnly || touched.current) return;
    st.current = { x0: e.clientX, y0: e.clientY, moved: false };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const move = (e) => {
    const s = st.current;
    if (!s) return;
    if (!s.moved && Math.hypot(e.clientX - s.x0, e.clientY - s.y0) < 4) return;
    s.moved = true;
    const el = document.elementsFromPoint(e.clientX, e.clientY).find((x) => x.dataset && x.dataset.date);
    setOver(el ? el.dataset.date : null);
    s.target = el ? el.dataset.date : null;
  };
  const up = (e) => {
    const s = st.current;
    st.current = null;
    setOver(null);
    if (!s) return;
    if (!s.moved) { onClick({ currentTarget: ref.current }); return; }
    if (!s.target) return;
    const target = days.find((d) => ymd(d) === s.target);
    if (!target) return;
    // Where it was grabbed is unknown, so the bar's first visible day moves to the drop day.
    const shiftDays = Math.round((startOfDay(target) - startOfDay(days[bar.startIdx])) / 86400000);
    if (!shiftDays) return;
    onDrop(bar, addDays(bar.start, shiftDays), addDays(bar.end, shiftDays));
  };
  return (
    <button ref={ref} type="button" data-occ onPointerDown={down} onPointerMove={move} onPointerUp={up}
      onClick={(e) => { if (bar.readOnly || e.detail === 0 || touched.current) onClick(e); }}
      aria-label={`${bar.title}${bar.participant ? `, ${bar.participant}` : ''}, all day`}
      className={clsx('absolute h-[22px] rounded-md px-2 text-[12px] font-semibold truncate text-left flex items-center gap-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
        selected && 'shadow-lg', over && 'opacity-60')}
      style={{
        top: bar.lane * 24 + 2, left: `calc(${left}% + 2px)`, width: `calc(${width}% - 6px)`,
        background: key ? tint(c.hex, 0.13) : c.hex, color: key ? c.hex : c.ink,
      }}>
      {bar.icon && <bar.icon size={12} aria-hidden className="flex-shrink-0" />}
      <span className={clsx('truncate', bar.status === 'cancelled' && 'line-through')}>{bar.title}{bar.participant && key ? ` · ${bar.participant}` : ''}</span>
    </button>
  );
}
